/**
 * The element-pinpointing behavior, shared by the staff FeedbackWidget and the
 * researcher portal's feedback modal: while picking, document-level CAPTURE
 * listeners take over — hover outlines the element under the cursor, click
 * captures its selector + viewport rect + label (and is swallowed, so picking
 * a button doesn't press it), Escape cancels. Anything inside a
 * `[data-feedback-widget]` subtree is ignored so the widgets can't pick
 * themselves.
 */
import { useEffect, useState } from 'react';
import type { ElementRegion, RegionRect } from '@/types/feedback';

/** The structural slice of Element the selector builder walks — typed
 *  structurally so the pure logic is testable without a DOM. */
export interface SelectorNode {
  tagName: string;
  id: string;
  parentElement: SelectorNode | null;
  children: ArrayLike<SelectorNode> | SelectorNode[];
  textContent: string | null;
}

/**
 * A short, human-legible selector for a clicked element: #id wins outright;
 * otherwise a tag:nth-of-type chain, capped so a deep tree yields a usable
 * breadcrumb rather than a novel. Best-effort by design — the label and rect
 * travel with it, so a selector that no longer resolves still says where and
 * what the element was.
 *
 * `stopAt` is document.body in the browser; tests pass their own root.
 */
export function buildElementSelector(el: SelectorNode, stopAt?: SelectorNode | null): string {
  const stop = stopAt !== undefined
    ? stopAt
    : (typeof document !== 'undefined' ? (document.body as unknown as SelectorNode) : null);
  const parts: string[] = [];
  let node: SelectorNode | null = el;
  while (node && node !== stop && parts.length < 6) {
    if (node.id) {
      parts.unshift(`#${node.id}`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    const parent: SelectorNode | null = node.parentElement;
    if (!parent) { parts.unshift(tag); break; }
    const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
    parts.unshift(sameTag.length > 1
      ? `${tag}:nth-of-type(${sameTag.indexOf(node) + 1})`
      : tag);
    node = parent;
  }
  return parts.join(' > ');
}

export function describeElement(el: SelectorNode): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ').slice(0, 60);
  return `${tag}${id}${text ? ` — “${text}”` : ''}`;
}

export function useElementPicker(args: {
  onPicked: (pin: ElementRegion) => void;
  onExit?: () => void;
}) {
  const [isPicking, setIsPicking] = useState(false);
  const [hoverRect, setHoverRect] = useState<RegionRect | null>(null);
  const { onPicked, onExit } = args;

  useEffect(() => {
    if (!isPicking) return;
    const ignore = (el: Element | null) =>
      !el || !!(el as HTMLElement).closest?.('[data-feedback-widget]');
    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (ignore(el)) { setHoverRect(null); return; }
      const r = el!.getBoundingClientRect();
      setHoverRect({ x: r.left, y: r.top, width: r.width, height: r.height });
    };
    const onClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (ignore(el)) return;
      const r = el!.getBoundingClientRect();
      onPicked({
        kind: 'element',
        selector: buildElementSelector(el!),
        label: describeElement(el!),
        rect: { x: r.left, y: r.top, width: r.width, height: r.height },
        space: 'viewport',
        viewport: { width: window.innerWidth, height: window.innerHeight },
      });
      setIsPicking(false);
      setHoverRect(null);
      onExit?.();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPicking(false);
        setHoverRect(null);
        onExit?.();
      }
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [isPicking, onPicked, onExit]);

  return { isPicking, setIsPicking, hoverRect };
}
